import { useState, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { fetchPlantReportData } from '../../services/api'

const PlantReport = () => {
  const [fromDate, setFromDate] = useState(dayjs())
  const [toDate, setToDate] = useState(dayjs())
  const [plantData, setPlantData] = useState([])
  const [summaryData, setSummaryData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('info')

  const fetchPlantData = async (startDate, endDate) => {
    setLoading(true)
    setError(null)

    try {
      const formattedStart = startDate.format('YYYY-MM-DD')
      const formattedEnd = endDate.format('YYYY-MM-DD')

      const result = await fetchPlantReportData(formattedStart, formattedEnd)

      setPlantData(result.data)
      setSummaryData(result.summary)
    } catch (err) {
      const errorMessage = err.message
      setError(errorMessage)
      setPlantData([])
      setSummaryData(null)

      setAlertMessage(errorMessage)
      setAlertSeverity('error')
      setAlertOpen(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPlantData(fromDate, toDate)
  }, [])

  useEffect(() => {
    if (fromDate && toDate) {
      fetchPlantData(fromDate, toDate)
    }
  }, [fromDate, toDate])

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue)
  }

  const handleToDateChange = (newValue) => {
    setToDate(newValue)
  }

  const handleAlertClose = () => {
    setAlertOpen(false)
  }

  const handleExport = () => {
    if (plantData.length === 0) {
      setAlertMessage('No data to export')
      setAlertSeverity('warning')
      setAlertOpen(true)
      return
    }

    try {
      const headers = [
        'Plant Name',
        'Inverter Energy (kWh)',
        'MFM Energy (kWh)',
        'Average Yield',
        'Average PR (%)',
        'Total GTI',
      ]

      const csvContent = [
        headers.join(','),
        ...plantData.map((plant) =>
          [
            `"${plant.plantName}"`,
            plant.inverterEnergy || 0,
            plant.mfmEnergy || 0,
            plant.averageYield || 0,
            plant.averagePR || 0,
            plant.total_gti || 0,
          ].join(',')
        ),
        ...(summaryData
          ? [
              [
                '"TOTAL/AVERAGE"',
                summaryData.totalInverterEnergy || 0,
                summaryData.totalMfmEnergy || 0,
                summaryData.overallAverageYield || 0,
                summaryData.overallAveragePR || 0,
                summaryData.overallAverageGTI || 0,
              ].join(','),
            ]
          : []),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute(
        'download',
        `plant_report_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format(
          'YYYY-MM-DD'
        )}.csv`
      )
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setAlertMessage('Report exported successfully!')
      setAlertSeverity('success')
      setAlertOpen(true)
    } catch (err) {
      setAlertMessage('Failed to export report')
      setAlertSeverity('error')
      setAlertOpen(true)
    }
  }

  const datePickerStyles = {
    '& .MuiInputBase-root': {
      color: '#000',
      backgroundColor: 'white',
      borderRadius: '50px',
      border: 'none',
      fontSize: '14px',
      boxShadow: '1px 4px 15px rgba(0, 0, 0, 0.05)',
      height: '40px',
      width: '130px',
    },
    '& .MuiOutlinedInput-root': {
      border: 'none',
      height: '40px',
      width: '198px',
    },
    '& .MuiOutlinedInput-input': {
      border: 'none',
      padding: '5px',
    },
    '& .MuiFormLabel-root': {
      backgroundColor: '#fff',
      padding: '0px 6px 0px 6px',
      borderRadius: '10px',
    },
    '& .MuiPickersDay-daySelected': {
      backgroundColor: 'white',
      boxShadow: '4px 4px 15px rgba(0, 0, 0, 0.05)',
      borderRadius: '50px',
      border: 'none',
      '&:hover': {
        backgroundColor: 'white',
        border: 'none',
      },
    },
  }

  const underlineStyle = {
    borderBottom: '1px solid #5968b7',
    marginBottom: '10px',
  }

  return (
    <div>
      {/* Header with Title and Date Filters - FIXED ALIGNMENT */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center', // Changed from 'baseline' to 'center'
          mb: 1,
          height: '56px', // Fixed height to match DatePicker input height
        }}
      >
        <Typography
          sx={{
            color: '#000',
            fontWeight: '400',
            fontSize: '24px',
            margin: 0, // Remove all margins
            lineHeight: '56px', // Match the container height
          }}
        >
          Plant Report
        </Typography>

        {/* Date Filter Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px', // Add spacing between date pickers
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer
              components={['DatePicker']}
              sx={{
                '& .MuiStack-root': {
                  margin: 0,
                  padding: 0,
                },
              }}
            >
              <DatePicker
                label="From Date"
                maxDate={toDate}
                minDate={dayjs(new Date()).subtract(2, 'year')}
                format="DD-MM-YYYY"
                value={fromDate}
                onChange={handleFromDateChange}
                sx={datePickerStyles}
                disabled={loading}
              />
            </DemoContainer>
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer
              components={['DatePicker']}
              sx={{
                '& .MuiStack-root': {
                  margin: 0,
                  padding: 0,
                },
              }}
            >
              <DatePicker
                label="To Date"
                maxDate={dayjs()}
                minDate={fromDate}
                format="DD-MM-YYYY"
                value={toDate}
                onChange={handleToDateChange}
                sx={datePickerStyles}
                disabled={loading}
              />
            </DemoContainer>
          </LocalizationProvider>
        </Box>
      </Box>

      {/* Underline */}
      <div style={underlineStyle}></div>

      {/* Custom Alert Component */}
      {alertOpen && (
        <Alert severity={alertSeverity} sx={{ mb: 2 }} onClose={handleAlertClose}>
          {alertMessage}
        </Alert>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Plant Report Table */}
      {!loading && (
        <TableContainer
          component={Paper}
          sx={{
            mb: 3,
            borderRadius: '12px',
            border: '1px solid #ddd',
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="plant report table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                  Plant
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', fontSize: '16px' }}
                  align="right"
                >
                  Inverter Energy (kWh)
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', fontSize: '16px' }}
                  align="right"
                >
                  MFM Energy (kWh)
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', fontSize: '16px' }}
                  align="right"
                >
                  Average Yield
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', fontSize: '16px' }}
                  align="right"
                >
                  Average PR (%)
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', fontSize: '16px' }}
                  align="right"
                >
                  Total GTI
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plantData.map((plant) => (
                <TableRow
                  key={plant.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {plant.plantName}
                  </TableCell>
                  <TableCell align="right">
                    {plant.inverterEnergy?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {plant.mfmEnergy?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {plant.averageYield?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {plant.averagePR?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {plant.total_gti?.toFixed(2) || '0.00'}
                  </TableCell>
                </TableRow>
              ))}

              {summaryData && plantData.length > 0 && (
                <TableRow
                  sx={{
                    borderTop: '2px solid #000',
                    '& td': { fontWeight: 'bold', fontSize: '16px' },
                  }}
                >
                  <TableCell component="th" scope="row">
                    TOTAL/AVERAGE
                  </TableCell>
                  <TableCell align="right">
                    {summaryData.totalInverterEnergy?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {summaryData.totalMfmEnergy?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {summaryData.overallAverageYield?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {summaryData.overallAveragePR?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right">
                    {summaryData.overallAverageGTI?.toFixed(2) || '0.00'}
                  </TableCell>
                </TableRow>
              )}

              {plantData.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No data available for selected date range
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Export Button */}
      {!loading && plantData.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleExport}
            sx={{
              borderRadius: '12px',
              padding: '10px 30px',
              textTransform: 'none',
              fontSize: '16px',
            }}
          >
            Export to CSV
          </Button>
        </Box>
      )}
    </div>
  )
}

export default PlantReport